import { useMemo, use, Suspense, ReactNode, memo } from 'react';
import { observer } from 'mobx-react-lite';
import { Participant } from 'livekit-client';
import { Icon, Tooltip, useTheme } from '@web-chat-core/ui-kit';
import { useTranslation } from 'react-i18next';
import { FEATURES } from 'consts';
import { useParticipant } from 'core';
import { useParticipantMetadata } from 'useCase/participant/useParticipantMetadata';
import { useParticipantItem } from 'features/ParticipantItem';
import { RaiseHandListIcon } from 'features/RaiseHand/components';
import { useRaiseHand } from 'features/RaiseHand/hooks/useRaiseHand';
import { ReactComponent as IconMicMutedNoShadow } from 'static/ic-mic-off-no-shadow-16.svg';
import { ReactComponent as IconAnimationCall } from 'static/ic-animation-call.svg';
import { selectIsParticipantSharingScreen, selectIsRecording, selectRecordingInitiatorData } from 'state/selectors';
import { RecordIcon } from 'features/Recording/components/RecordIcon/RecordIcon';
import { isGuestParticipant, isMainModeratorParticipant, isModeratorParticipant } from 'useCase';
import { isParticipantSpeaker } from 'useCase/webinar/webinarStatus';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { ModerationActionsMenu } from 'features/Moderation/components/ModerationActionsMenu/ModerationActionsMenu';
import {
    ListIconsContainer,
    ListIconWrapper,
    ListItem,
    ListItemAvatar,
    ListItemAvatarImage,
    ListItemGuestLabel,
    ListItemIconAnimated,
    ListItemNameWrapper,
    ListItemText,
    ListItemUserInfo,
} from './view';

interface IListItemProps {
    participant: Participant;
}

interface IAvatarProps {
    participant: Participant;
    name: string;
    placeholder: ReactNode;
}

const Avatar = memo(({ participant, name, placeholder }: IAvatarProps) => {
    const { getAvatarFromMetadata } = useParticipantMetadata();
    const result = getAvatarFromMetadata(participant.metadata);
    const avatar = typeof result === 'string' ? result : use(result);
    return avatar ? <ListItemAvatarImage src={avatar} alt={name} draggable="false" /> : <>{placeholder}</>;
});

export const ParticipantListItem = observer(({ participant }: IListItemProps) => {
    const { isLocal, isSpeaking, microphonePublication } = useParticipant(participant);
    const { getDisplayData } = useParticipantItem();
    const { currentName, name, placeholder } = useMemo(
        () => getDisplayData(participant, isLocal),
        [participant, isLocal],
    );
    const isMuted = !microphonePublication || microphonePublication.isMuted;
    const showSpeakingIcon = isSpeaking && !isMuted;
    const showMutedIcon = !isSpeaking && isMuted;

    const theme = useTheme();
    const { t } = useTranslation();

    const { checkIsHandRaised } = useRaiseHand();
    const isHandRaised = checkIsHandRaised(participant);
    // При отдельной секции поднятых рук иконку в общем списке не дублируем (для Tango секции нет — иконка видна)
    const showRaisedHandInList = isHandRaised && !isFeatureOn(FEATURES.LOCAL_VCS_RAISED_HANDS_LIST_ENABLED);

    const isRecording = isFeatureOn(FEATURES.VCS_RECORDING_ENABLED) && selectIsRecording();
    const initiatorData = selectRecordingInitiatorData();
    // Бэк не даёт возможности делать запрос за id пользователей, определяем через имя.
    const isParticipantInitRecording = initiatorData && initiatorData.initiatorName === name;
    const isRecordingIconShown = isRecording && isParticipantInitRecording;

    const isSharingScreen = selectIsParticipantSharingScreen(participant);
    const isModerator = isModeratorParticipant(participant);
    const isMainModerator = isMainModeratorParticipant(participant);
    const isGuest = isGuestParticipant(participant);
    const isSpeaker = isParticipantSpeaker(participant);

    return (
        <ListItem>
            <ListItemUserInfo>
                <ListItemAvatar sid={participant.sid}>
                    <Suspense fallback={placeholder}>
                        <Avatar participant={participant} name={name} placeholder={placeholder} />
                    </Suspense>
                </ListItemAvatar>
                {isGuest ? (
                    <ListItemNameWrapper>
                        <ListItemText>{currentName}</ListItemText>
                        <ListItemGuestLabel>{t('vks_request_guest')}</ListItemGuestLabel>
                    </ListItemNameWrapper>
                ) : (
                    <ListItemText>{currentName}</ListItemText>
                )}
            </ListItemUserInfo>

            <ListIconsContainer>
                {isModerator && (
                    <Tooltip
                        label={t(
                            isMainModerator
                                ? 'moderator_primary_meeting_moderator'
                                : 'web_videoconference_meeting_moderator',
                        )}
                    >
                        <ListIconWrapper>
                            <Icon icon="icModerator24" size={16} color={theme.root.colors.basicColors.white['50%']} />
                        </ListIconWrapper>
                    </Tooltip>
                )}
                {isSpeaker && !isModerator && (
                    <Tooltip label={t('vks_speaker')}>
                        <ListIconWrapper>
                            <Icon icon="icSpeaker16" size={16} color={theme.root.colors.basicColors.white['50%']} />
                        </ListIconWrapper>
                    </Tooltip>
                )}
                {isSharingScreen && (
                    <ListIconWrapper>
                        <Icon icon="icDesktop16" size={16} color={theme.root.colors.basicColors.white['50%']} />
                    </ListIconWrapper>
                )}

                {showSpeakingIcon && (
                    <ListIconWrapper>
                        <ListItemIconAnimated>
                            <IconAnimationCall />
                        </ListItemIconAnimated>
                    </ListIconWrapper>
                )}

                {isRecordingIconShown && (
                    <ListIconWrapper>
                        <RecordIcon />
                    </ListIconWrapper>
                )}

                {showRaisedHandInList && (
                    <ListIconWrapper>
                        <RaiseHandListIcon />
                    </ListIconWrapper>
                )}

                {showMutedIcon && (
                    <ListIconWrapper>
                        <IconMicMutedNoShadow />
                    </ListIconWrapper>
                )}

                <ModerationActionsMenu participant={participant} variant="list" />
            </ListIconsContainer>
        </ListItem>
    );
});
